import { Chat, Message, SearchResult } from '../types';

interface SearchOptions {
  query: string;
  chatId?: string;
  sender?: string;
  dateFrom?: Date;
  dateTo?: Date;
  caseSensitive?: boolean;
  useRegex?: boolean;
}

export class SearchIndex {
  private chats: Map<string, Chat>;

  constructor() {
    this.chats = new Map();
  }

  /**
   * Index chats for searching
   */
  public indexChats(chats: Chat[]): void {
    this.chats.clear();
    chats.forEach((chat) => {
      this.chats.set(chat.id, chat);
    });
  }

  /**
   * Normalize text for searching (lowercase, trim)
   */
  private normalizeText(text: string, caseSensitive: boolean = false): string {
    return caseSensitive ? text : text.toLowerCase();
  }

  /**
   * Create a snippet around matched text
   */
  private createSnippet(text: string, matchIndex: number, matchLength: number): string {
    const snippetLength = 100;
    const start = Math.max(0, matchIndex - snippetLength / 2);
    const end = Math.min(text.length, matchIndex + matchLength + snippetLength / 2);
    
    let snippet = text.substring(start, end);
    
    if (start > 0) snippet = '...' + snippet;
    if (end < text.length) snippet = snippet + '...';
    
    return snippet;
  }

  /**
   * Check if text matches query (supports regex and fuzzy matching)
   */
  private matchesQuery(
    text: string,
    query: string,
    options: SearchOptions
  ): { matches: boolean; index: number; length: number } {
    const normalizedText = this.normalizeText(text, options.caseSensitive);
    const normalizedQuery = this.normalizeText(query, options.caseSensitive);

    if (options.useRegex) {
      try {
        const flags = options.caseSensitive ? 'g' : 'gi';
        const regex = new RegExp(query, flags);
        const match = regex.exec(text);
        if (match) {
          return { matches: true, index: match.index, length: match[0].length };
        }
      } catch (error) {
        console.warn('Invalid regex:', error);
      }
      return { matches: false, index: -1, length: 0 };
    }

    const index = normalizedText.indexOf(normalizedQuery);
    if (index !== -1) {
      return { matches: true, index, length: query.length };
    }

    // Fuzzy matching for typos (simple implementation)
    if (!options.useRegex && this.fuzzyMatch(normalizedText, normalizedQuery)) {
      const fuzzyIndex = normalizedText.indexOf(normalizedQuery.substring(0, normalizedQuery.length - 1));
      if (fuzzyIndex !== -1) {
        return { matches: true, index: fuzzyIndex, length: query.length - 1 };
      }
    }

    return { matches: false, index: -1, length: 0 };
  }

  /**
   * Simple fuzzy matching (allows 1 character difference)
   */
  private fuzzyMatch(text: string, query: string): boolean {
    if (query.length < 3) return false;
    
    // Check if text contains most of the query characters
    const queryChars = query.split('');
    let matchCount = 0;
    
    for (const char of queryChars) {
      if (text.includes(char)) {
        matchCount++;
      }
    }
    
    return matchCount >= query.length - 1;
  }

  /**
   * Filter message by date range
   */
  private isInDateRange(message: Message, dateFrom?: Date, dateTo?: Date): boolean {
    if (!dateFrom && !dateTo) return true;
    
    const messageDate = new Date(message.timestamp);
    
    if (dateFrom && messageDate < dateFrom) return false;
    if (dateTo && messageDate > dateTo) return false;
    
    return true;
  }

  /**
   * Search messages across all chats or within a specific chat
   */
  public search(options: SearchOptions): SearchResult[] {
    const results: SearchResult[] = [];
    const { query, chatId, sender, dateFrom, dateTo } = options;

    if (!query.trim()) return results;

    const chatsToSearch = chatId 
      ? [this.chats.get(chatId)].filter(Boolean) as Chat[]
      : Array.from(this.chats.values());

    for (const chat of chatsToSearch) {
      for (const message of chat.messages) {
        // Filter by sender
        if (sender && message.sender?.toLowerCase() !== sender.toLowerCase()) {
          continue;
        }

        // Filter by date range
        if (!this.isInDateRange(message, dateFrom, dateTo)) {
          continue;
        }

        // Search in message text
        const textMatch = this.matchesQuery(message.text, query, options);
        
        // Search in sender name
        const senderMatch = message.sender 
          ? this.matchesQuery(message.sender, query, options)
          : { matches: false, index: -1, length: 0 };

        if (textMatch.matches || senderMatch.matches) {
          const matchInfo = textMatch.matches ? textMatch : senderMatch;
          const matchedText = textMatch.matches 
            ? message.text.substring(matchInfo.index, matchInfo.index + matchInfo.length)
            : message.sender || '';

          results.push({
            chatId: chat.id,
            messageId: message.id,
            message,
            chatTitle: chat.title,
            snippet: this.createSnippet(message.text, matchInfo.index, matchInfo.length),
            matchedText,
          });
        }
      }
    }

    return results;
  }

  /**
   * Search chats by title or participants
   */
  public searchChats(query: string): Chat[] {
    if (!query.trim()) return Array.from(this.chats.values());

    const normalizedQuery = query.toLowerCase();
    const results: Chat[] = [];

    for (const chat of this.chats.values()) {
      const titleMatch = chat.title.toLowerCase().includes(normalizedQuery);
      const participantMatch = chat.participants.some((p) =>
        p.toLowerCase().includes(normalizedQuery)
      );

      if (titleMatch || participantMatch) {
        results.push(chat);
      }
    }

    return results;
  }

  /**
   * Get all indexed chats
   */
  public getAllChats(): Chat[] {
    return Array.from(this.chats.values());
  }

  /**
   * Get a specific chat by ID
   */
  public getChat(chatId: string): Chat | undefined {
    return this.chats.get(chatId);
  }
}
